import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/useToast';
import { FiSave, FiTrash2, FiPlus, FiCheck, FiArrowLeft } from 'react-icons/fi';
import walletApi from '@/services/api/wallet';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { BankAccount, MobileMoneyAccount, MobileMoneyProvider } from '@/types/wallet';

interface WalletSettings {
  withdrawalSettings: {
    autoWithdraw: boolean;
    autoWithdrawThreshold: number;
    minWithdrawalAmount: number;
      withdrawalMethod: 'bank_transfer' | 'mobile_money' | 'paystack_transfer';
  };
  bankAccounts: BankAccount[];
  mobileMoneyAccounts: MobileMoneyAccount[];
  dailyWithdrawalLimit: number;
  monthlyWithdrawalLimit: number;
  requiresTwoFactorForWithdrawal: boolean;
  status: string;
}

const WalletSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<WalletSettings | null>(null);
  const [showAddBank, setShowAddBank] = useState(false);
  const [showAddMobile, setShowAddMobile] = useState(false);

  const [newBankAccount, setNewBankAccount] = useState({
    accountName: '',
    accountNumber: '',
    bankName: '',
    bankCode: '',
    branchCode: '',
    isDefault: false,
  });

  const [newMobileAccount, setNewMobileAccount] = useState({
    provider: 'mtn' as MobileMoneyProvider,
    phoneNumber: '',
    accountName: '',
    isDefault: false,
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await walletApi.getWalletSettings();
      setSettings(response.data.data);
    } catch (error: any) {
      addToast(error.response?.data?.error?.message || 'Failed to load wallet settings', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!settings) return;

    try {
      setSaving(true);
      await walletApi.updateWalletSettings(settings);
      addToast('Wallet settings updated successfully', 'success');
    } catch (error: any) {
      addToast(error.response?.data?.error?.message || 'Failed to update settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleAddBankAccount = () => {
    if (!settings) return;

    if (!newBankAccount.accountName || !newBankAccount.accountNumber || !newBankAccount.bankName || !newBankAccount.bankCode) {
      addToast('Please fill in all required fields', 'error');
      return;
    }

    const updatedBankAccounts = [...settings.bankAccounts, {
      ...newBankAccount,
      verified: false,
      createdAt: new Date().toISOString(),
    }];

    setSettings({
      ...settings,
      bankAccounts: updatedBankAccounts,
    });

    setNewBankAccount({
      accountName: '',
      accountNumber: '',
      bankName: '',
      bankCode: '',
      branchCode: '',
      isDefault: false,
    });

    setShowAddBank(false);
    addToast('Bank account added. Click Save to confirm.', 'success');
  };

  const handleAddMobileAccount = () => {
    if (!settings) return;

    if (!newMobileAccount.phoneNumber || !newMobileAccount.accountName) {
      addToast('Please fill in all required fields', 'error');
      return;
    }

    const updatedMobileAccounts = [...settings.mobileMoneyAccounts, {
      ...newMobileAccount,
      verified: false,
      createdAt: new Date().toISOString(),
    }];

    setSettings({
      ...settings,
      mobileMoneyAccounts: updatedMobileAccounts,
    });

    setNewMobileAccount({
      provider: 'mtn',
      phoneNumber: '',
      accountName: '',
      isDefault: false,
    });

    setShowAddMobile(false);
    addToast('Mobile money account added. Click Save to confirm.', 'success');
  };

  const handleRemoveBankAccount = (index: number) => {
    if (!settings) return;

    const updatedBankAccounts = settings.bankAccounts.filter((_, i) => i !== index);
    setSettings({
      ...settings,
      bankAccounts: updatedBankAccounts,
    });
    addToast('Bank account removed. Click Save to confirm.', 'success');
  };

  const handleRemoveMobileAccount = (index: number) => {
    if (!settings) return;

    const updatedMobileAccounts = settings.mobileMoneyAccounts.filter((_, i) => i !== index);
    setSettings({
      ...settings,
      mobileMoneyAccounts: updatedMobileAccounts,
    });
    addToast('Mobile money account removed. Click Save to confirm.', 'success');
  };

  const handleSetDefaultBank = (index: number) => {
    if (!settings) return;

    const updatedBankAccounts = settings.bankAccounts.map((acc, i) => ({
      ...acc,
      isDefault: i === index,
    }));

    setSettings({
      ...settings,
      bankAccounts: updatedBankAccounts,
    });
  };

  const handleSetDefaultMobile = (index: number) => {
    if (!settings) return;

    const updatedMobileAccounts = settings.mobileMoneyAccounts.map((acc, i) => ({
      ...acc,
      isDefault: i === index,
    }));

    setSettings({
      ...settings,
      mobileMoneyAccounts: updatedMobileAccounts,
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <LoadingSpinner />
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="py-6">
        <p className="text-center text-slate-600 dark:text-gray-400">Failed to load wallet settings</p>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Wallet Settings - SideGO</title>
      </Helmet>

      <div className="relative py-1 sm:py-2">
        <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-72 bg-gradient-to-b from-cyan-100/60 to-transparent dark:from-cyan-900/20" />

        <div className="mb-3 sm:mb-4">
          <button
            onClick={() => navigate('/dashboard/wallet')}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            <FiArrowLeft className="h-4 w-4" />
            Back to Wallet
          </button>
        </div>

        <div className="mb-4 overflow-hidden rounded-3xl border border-slate-200 bg-white/90 p-4 shadow-sm backdrop-blur dark:border-gray-800 dark:bg-gray-900/85 sm:mb-8 sm:p-6">
          <div className="mb-3 flex items-center justify-between gap-2 sm:mb-4">
            <span className="inline-flex items-center whitespace-nowrap rounded-full border border-slate-200 bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
              Wallet Controls
            </span>
            <span className="inline-flex items-center whitespace-nowrap rounded-full border border-cyan-200 bg-cyan-50 px-3 py-1 text-xs font-semibold text-cyan-700 dark:border-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300">
              Secure Setup
            </span>
          </div>

          <h1 className="text-2xl font-bold text-slate-900 dark:text-white sm:text-3xl">Wallet Settings</h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-gray-400 sm:text-base">
            Manage your wallet preferences, payment methods, and withdrawal settings
          </p>
        </div>

        <div className="space-y-4 sm:space-y-6">
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900 sm:p-6">
            <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white sm:text-xl">
              Withdrawal Settings
            </h2>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-slate-700 dark:text-gray-300">
                    Auto Withdraw
                  </label>
                  <p className="text-xs text-slate-500 dark:text-gray-400">
                    Automatically withdraw when balance reaches threshold
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.withdrawalSettings.autoWithdraw}
                    aria-label="Enable auto withdraw"
                    onChange={(e) =>
                      setSettings({
                        ...settings,
                        withdrawalSettings: {
                          ...settings.withdrawalSettings,
                          autoWithdraw: e.target.checked,
                        },
                      })
                    }
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-neutral-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-neutral-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-neutral-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-neutral-600 peer-checked:bg-primary-600"></div>
                </label>
              </div>

              {settings.withdrawalSettings.autoWithdraw && (
                <div>
                  <label htmlFor="wallet-auto-withdraw-threshold" className="mb-2 block text-sm font-medium text-slate-700 dark:text-gray-300">
                    Auto Withdraw Threshold (GHS)
                  </label>
                  <input
                    id="wallet-auto-withdraw-threshold"
                    type="number"
                    value={settings.withdrawalSettings.autoWithdrawThreshold}
                    onChange={(e) =>
                      setSettings({
                        ...settings,
                        withdrawalSettings: {
                          ...settings.withdrawalSettings,
                          autoWithdrawThreshold: Number(e.target.value),
                        },
                      })
                    }
                    className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2 text-slate-900 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                  />
                </div>
              )}

              <div>
                <label htmlFor="wallet-min-withdrawal-amount" className="mb-2 block text-sm font-medium text-slate-700 dark:text-gray-300">
                  Minimum Withdrawal Amount (GHS)
                </label>
                <input
                  id="wallet-min-withdrawal-amount"
                  type="number"
                  value={settings.withdrawalSettings.minWithdrawalAmount}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      withdrawalSettings: {
                        ...settings.withdrawalSettings,
                        minWithdrawalAmount: Number(e.target.value),
                      },
                    })
                  }
                  className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2 text-slate-900 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                />
              </div>

              <div>
                <label htmlFor="wallet-withdrawal-method" className="mb-2 block text-sm font-medium text-slate-700 dark:text-gray-300">
                  Preferred Withdrawal Method
                </label>
                <select
                  id="wallet-withdrawal-method"
                  value={settings.withdrawalSettings.withdrawalMethod}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      withdrawalSettings: {
                        ...settings.withdrawalSettings,
                        withdrawalMethod: e.target.value as any,
                      },
                    })
                  }
                  className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2 text-slate-900 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                >
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="mobile_money">Mobile Money</option>
                  <option value="paystack">Paystack</option>
                </select>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900 sm:p-6">
            <div className="mb-4 flex items-center justify-between gap-2">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white sm:text-xl">
                Bank Accounts
              </h2>
              <button
                onClick={() => setShowAddBank(!showAddBank)}
                title="Add Bank Account"
                aria-label="Add Bank Account"
                className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-primary-600 text-white transition-colors hover:bg-primary-700 sm:h-auto sm:w-auto sm:gap-1.5 sm:px-3 sm:py-1.5 sm:text-sm"
              >
                <FiPlus className="h-4 w-4" />
                <span className="hidden sm:inline">Add Bank Account</span>
              </button>
            </div>

            {showAddBank && (
              <div className="mb-4 space-y-3 rounded-xl bg-slate-50 p-4 dark:bg-gray-800">
                <input
                  type="text"
                  placeholder="Account Name"
                  aria-label="Bank account name"
                  value={newBankAccount.accountName}
                  onChange={(e) => setNewBankAccount({ ...newBankAccount, accountName: e.target.value })}
                  className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2 text-slate-900 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
                />
                <input
                  type="text"
                  placeholder="Account Number"
                  aria-label="Bank account number"
                  value={newBankAccount.accountNumber}
                  onChange={(e) => setNewBankAccount({ ...newBankAccount, accountNumber: e.target.value })}
                  className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2 text-slate-900 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
                />
                <input
                  type="text"
                  placeholder="Bank Name"
                  aria-label="Bank name"
                  value={newBankAccount.bankName}
                  onChange={(e) => setNewBankAccount({ ...newBankAccount, bankName: e.target.value })}
                  className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2 text-slate-900 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
                />
                <input
                  type="text"
                  placeholder="Bank Code"
                  aria-label="Bank code"
                  value={newBankAccount.bankCode}
                  onChange={(e) => setNewBankAccount({ ...newBankAccount, bankCode: e.target.value })}
                  className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2 text-slate-900 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
                />
                <input
                  type="text"
                  placeholder="Branch Code (Optional)"
                  aria-label="Branch code optional"
                  value={newBankAccount.branchCode}
                  onChange={(e) => setNewBankAccount({ ...newBankAccount, branchCode: e.target.value })}
                  className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2 text-slate-900 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
                />
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={handleAddBankAccount}
                    className="rounded-xl bg-green-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-green-700 sm:text-sm"
                  >
                    Add
                  </button>
                  <button
                    onClick={() => setShowAddBank(false)}
                    className="rounded-xl border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800 sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            <div className="space-y-3">
              {settings.bankAccounts.length === 0 ? (
                <p className="py-4 text-center text-slate-500 dark:text-gray-400">
                  No bank accounts added yet
                </p>
              ) : (
                settings.bankAccounts.map((account, index) => (
                  <div
                    key={index}
                    className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50/40 p-3 dark:border-gray-800 dark:bg-gray-900/40 sm:flex-row sm:items-center sm:justify-between sm:p-4"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-slate-900 dark:text-white">
                          {account.accountName}
                        </p>
                        {account.isDefault && (
                          <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-xs rounded-full flex items-center gap-1">
                            <FiCheck size={12} /> Default
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-600 dark:text-gray-400">
                        {account.bankName} - {account.accountNumber}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                      {!account.isDefault && (
                        <button
                          onClick={() => handleSetDefaultBank(index)}
                          className="rounded-lg bg-blue-100 px-2.5 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50"
                        >
                          Set Default
                        </button>
                      )}
                      <button
                        onClick={() => handleRemoveBankAccount(index)}
                        aria-label={`Remove bank account ${account.accountName}`}
                        className="rounded-lg p-1.5 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                      >
                        <FiTrash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900 sm:p-6">
            <div className="mb-4 flex items-center justify-between gap-2">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white sm:text-xl">
                Mobile Money Accounts
              </h2>
              <button
                onClick={() => setShowAddMobile(!showAddMobile)}
                title="Add Mobile Money"
                aria-label="Add Mobile Money"
                className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-primary-600 text-white transition-colors hover:bg-primary-700 sm:h-auto sm:w-auto sm:gap-1.5 sm:px-3 sm:py-1.5 sm:text-sm"
              >
                <FiPlus className="h-4 w-4" />
                <span className="hidden sm:inline">Add Mobile Money</span>
              </button>
            </div>

            {showAddMobile && (
              <div className="mb-4 space-y-3 rounded-xl bg-slate-50 p-4 dark:bg-gray-800">
                <select
                  value={newMobileAccount.provider}
                  aria-label="Mobile money provider"
                  onChange={(e) => setNewMobileAccount({ ...newMobileAccount, provider: e.target.value as MobileMoneyProvider })}
                  className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2 text-slate-900 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
                >
                  <option value="mtn">MTN Mobile Money</option>
                  <option value="vodafone">Vodafone Cash</option>
                  <option value="airteltigo">AirtelTigo Money</option>
                </select>
                <input
                  type="text"
                  placeholder="Phone Number"
                  aria-label="Mobile money phone number"
                  value={newMobileAccount.phoneNumber}
                  onChange={(e) => setNewMobileAccount({ ...newMobileAccount, phoneNumber: e.target.value })}
                  className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2 text-slate-900 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
                />
                <input
                  type="text"
                  placeholder="Account Name"
                  aria-label="Mobile money account name"
                  value={newMobileAccount.accountName}
                  onChange={(e) => setNewMobileAccount({ ...newMobileAccount, accountName: e.target.value })}
                  className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2 text-slate-900 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
                />
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={handleAddMobileAccount}
                    className="rounded-xl bg-green-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-green-700 sm:text-sm"
                  >
                    Add
                  </button>
                  <button
                    onClick={() => setShowAddMobile(false)}
                    className="rounded-xl border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800 sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            <div className="space-y-3">
              {settings.mobileMoneyAccounts.length === 0 ? (
                <p className="py-4 text-center text-slate-500 dark:text-gray-400">
                  No mobile money accounts added yet
                </p>
              ) : (
                settings.mobileMoneyAccounts.map((account, index) => (
                  <div
                    key={index}
                    className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50/40 p-3 dark:border-gray-800 dark:bg-gray-900/40 sm:flex-row sm:items-center sm:justify-between sm:p-4"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-slate-900 dark:text-white">
                          {account.accountName}
                        </p>
                        {account.isDefault && (
                          <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-xs rounded-full flex items-center gap-1">
                            <FiCheck size={12} /> Default
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-600 dark:text-gray-400">
                        {account.provider.toUpperCase()} - {account.phoneNumber}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                      {!account.isDefault && (
                        <button
                          onClick={() => handleSetDefaultMobile(index)}
                          className="rounded-lg bg-blue-100 px-2.5 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50"
                        >
                          Set Default
                        </button>
                      )}
                      <button
                        onClick={() => handleRemoveMobileAccount(index)}
                        aria-label={`Remove mobile money account ${account.accountName}`}
                        className="rounded-lg p-1.5 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                      >
                        <FiTrash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900 sm:p-6">
            <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white sm:text-xl">
              Security & Limits
            </h2>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-slate-700 dark:text-gray-300">
                    Two-Factor Authentication for Withdrawals
                  </label>
                  <p className="text-xs text-slate-500 dark:text-gray-400">
                    Require 2FA code for all withdrawals
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.requiresTwoFactorForWithdrawal}
                    aria-label="Require two-factor authentication for withdrawals"
                    onChange={(e) =>
                      setSettings({
                        ...settings,
                        requiresTwoFactorForWithdrawal: e.target.checked,
                      })
                    }
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-neutral-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-neutral-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-neutral-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-neutral-600 peer-checked:bg-primary-600"></div>
                </label>
              </div>

              <div>
                <label htmlFor="wallet-daily-withdrawal-limit" className="mb-2 block text-sm font-medium text-slate-700 dark:text-gray-300">
                  Daily Withdrawal Limit (GHS)
                </label>
                <input
                  id="wallet-daily-withdrawal-limit"
                  type="number"
                  value={settings.dailyWithdrawalLimit}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      dailyWithdrawalLimit: Number(e.target.value),
                    })
                  }
                  className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2 text-slate-900 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                />
              </div>

              <div>
                <label htmlFor="wallet-monthly-withdrawal-limit" className="mb-2 block text-sm font-medium text-slate-700 dark:text-gray-300">
                  Monthly Withdrawal Limit (GHS)
                </label>
                <input
                  id="wallet-monthly-withdrawal-limit"
                  type="number"
                  value={settings.monthlyWithdrawalLimit}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      monthlyWithdrawalLimit: Number(e.target.value),
                    })
                  }
                  className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2 text-slate-900 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:justify-end sm:gap-3">
            <button
              onClick={() => loadSettings()}
              className="rounded-xl border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800 sm:px-6 sm:py-2 sm:text-sm"
            >
              Reset
            </button>
            <button
              onClick={handleSaveSettings}
              disabled={saving}
              className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-primary-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50 sm:px-6 sm:py-2 sm:text-sm"
            >
              {saving ? (
                <>
                  <LoadingSpinner />
                  Saving...
                </>
              ) : (
                <>
                  <FiSave />
                  Save Settings
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default WalletSettingsPage;
